/**
 * Janitorial Product Detail Page — /marketplace/janitorial/<categorySlug>/<sku>
 * Renders ALL details scraped from sterilemate.com — image gallery,
 * description, key-features lists, specs, downloadable PDFs — fully
 * self-hosted, NO outbound links to sterilemate.com.
 */

import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowLeft, ShoppingCart, Plus, Phone, Download, FileText,
  CheckCircle2, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import {
  JANITORIAL_PRODUCTS,
  getCategoryBySlug,
  getProductsByCategory,
} from "@/data/janitorialCatalog";
import { getProductDetails } from "@/data/janitorialCatalogDetails";
import MarketplaceCategoryNav from "@/components/MarketplaceCategoryNav";
import MarketplaceBreadcrumb from "@/components/MarketplaceBreadcrumb";

export default function JanitorialProductPage({ basePath = "/marketplace/janitorial" }) {
  const { categorySlug, sku } = useParams();
  const [activeImg, setActiveImg] = useState(0);
  const { addItem, setShowCart, cart } = useQuoteCart();

  const cat = getCategoryBySlug(categorySlug);
  const product = JANITORIAL_PRODUCTS.find(p => p.sku === sku);
  const details = getProductDetails(sku);

  if (!cat || !product) return <Navigate to={basePath} replace />;

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  // Image gallery: prefer scraped images, fall back to thumbnail
  const galleryImgs = (details?.images && details.images.length > 0)
    ? details.images
    : [product.image];

  const handleAdd = () => {
    addItem({
      erp: product.sku,
      name: product.name,
      image: product.image,
      source: "janitorial",
      category: cat.name,
    });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`janitorial-product-${sku}`}>
      <MarketplaceCategoryNav currentCategory="janitorial-evs" />
      <MarketplaceBreadcrumb trail={[{ label: "Janitorial & EVS", to: basePath }, { label: cat.name, to: `${basePath}/${categorySlug}` }, { label: product.name }]} />
      {/* Top bar */}
      <header className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to={`${basePath}/${categorySlug}`}
            className="flex items-center gap-2 text-slate-300 hover:text-white"
            data-testid="back-to-category-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back to {cat.name}</span>
          </Link>
          <div className="flex items-center gap-3">
            <a href="tel:2814563458" className="hidden sm:flex items-center gap-1.5 text-sm text-slate-300 hover:text-white">
              <Phone className="w-4 h-4" /> 281.456.3458
            </a>
            <Button
              onClick={() => setShowCart(true)}
              size="sm"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold relative"
              data-testid="prod-open-cart-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-1.5" /> Quote Cart
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center" data-testid="prod-cart-count">
                  {cartCount}
                </span>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-1.5 text-xs text-slate-500">
          <Link to={basePath} className="hover:text-emerald-700">Janitorial / EVS</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`${basePath}/${categorySlug}`} className="hover:text-emerald-700">{cat.name}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-slate-700 font-medium">{product.name}</span>
        </div>
      </div>

      {/* HERO — gallery + summary + CTA */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-[1.1fr_1fr] gap-12">
          {/* Image gallery */}
          <div>
            <div className="aspect-square bg-white border border-slate-200 rounded-2xl p-8 flex items-center justify-center">
              <img
                src={galleryImgs[activeImg]}
                alt={product.name}
                className="max-w-full max-h-full object-contain"
                data-testid="product-hero-img"
                onError={(e) => { e.currentTarget.src = product.image; }}
              />
            </div>
            {galleryImgs.length > 1 && (
              <div className="grid grid-cols-6 gap-2 mt-3" data-testid="product-thumbnails">
                {galleryImgs.slice(0, 6).map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    data-testid={`thumb-${i}`}
                    className={`aspect-square bg-white border-2 rounded-lg overflow-hidden p-1 transition-all ${
                      activeImg === i ? "border-emerald-600 shadow-md" : "border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right — details */}
          <div>
            <Badge className="bg-slate-100 text-slate-700 border border-slate-200 mb-3">
              {cat.name}
            </Badge>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-2 leading-[1.1]" data-testid="product-title">
              {product.name}
            </h1>
            <p className="text-sm text-slate-500 mb-5">SKU {product.sku}</p>

            {details?.lead && details.lead.length > 0 && (
              <div className="space-y-3 mb-6 text-slate-700 leading-relaxed">
                {details.lead.map((p, i) => (
                  <p key={i} className="text-base">{p}</p>
                ))}
              </div>
            )}

            {product.description && !details?.lead?.length && (
              <p className="text-slate-700 leading-relaxed mb-6">{product.description}</p>
            )}

            {/* Pricing tiers — present for catalog-augment SKUs that ship with explicit unit/box/case pricing */}
            {product.pricing && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5" data-testid="product-pricing-tiers">
                <p className="text-xs font-bold text-amber-900 uppercase tracking-wide mb-2">Pricing</p>
                <ul className="text-sm text-slate-800 space-y-1.5">
                  {product.pricing.unit && <li className="flex justify-between gap-3"><span className="text-slate-600">Each</span><strong>{product.pricing.unit}</strong></li>}
                  {product.pricing.box && <li className="flex justify-between gap-3"><span className="text-slate-600">Box</span><strong>{product.pricing.box}</strong></li>}
                  {product.pricing.case && <li className="flex justify-between gap-3"><span className="text-slate-600">Case</span><strong>{product.pricing.case}</strong></li>}
                </ul>
                {product.pricing.note && <p className="text-[11px] text-amber-800 mt-2 italic">{product.pricing.note}</p>}
              </div>
            )}

            {/* CTA */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-5 mb-5">
              <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide mb-1">Sterile Mate Quote Cart</p>
              <p className="text-sm text-slate-700 mb-3">Add this to your cart and our team responds within 2 business hours with shipping &amp; volume pricing.</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="lg"
                  onClick={() => { handleAdd(); setShowCart(true); }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-md flex-1"
                  data-testid="product-add-and-view-btn"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" /> Add to Quote &amp; View Cart
                </Button>
                <Button
                  size="lg"
                  onClick={handleAdd}
                  variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="product-quick-add-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Quick Add
                </Button>
              </div>
            </div>

            {/* Top-level PDF downloads (compact tile) */}
            {details?.pdfs && details.pdfs.length > 0 && (
              <div className="space-y-2" data-testid="product-pdf-list">
                <p className="text-xs uppercase font-bold text-slate-500 tracking-wider mb-2">Downloads</p>
                {details.pdfs.map((p, i) => (
                  <a
                    key={i}
                    href={p.file}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    data-testid={`product-pdf-${i}`}
                    className="flex items-center justify-between gap-3 px-4 py-3 bg-white border border-slate-200 rounded-lg hover:border-amber-400 hover:shadow-sm transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-amber-700" />
                      </div>
                      <span className="text-sm font-medium text-slate-800">{p.label}</span>
                    </div>
                    <Download className="w-4 h-4 text-amber-600 group-hover:translate-y-0.5 transition-transform" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CONTENT SECTIONS — features, specs, etc. */}
      {details?.sections && details.sections.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-6" data-testid="product-sections">
          {details.sections.map((s, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm"
              data-testid={`section-${i}`}
            >
              <h2 className="text-lg font-bold text-slate-900 mb-4">{s.heading}</h2>
              {s.paragraphs && s.paragraphs.length > 0 && (
                <div className="space-y-2 mb-4 text-slate-700 text-sm leading-relaxed">
                  {s.paragraphs.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                </div>
              )}
              {s.items && s.items.length > 0 && (
                <ul className="space-y-2">
                  {s.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </section>
      )}

      {/* RELATED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">Related products in {cat.name}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {getProductsByCategory(categorySlug).filter(p => p.sku !== sku).slice(0, 6).map(p => (
            <Link
              key={p.sku}
              to={`${basePath}/${categorySlug}/${p.sku}`}
              className="group bg-white rounded-lg overflow-hidden border border-slate-200 hover:border-emerald-400 hover:shadow-md transition-all"
              data-testid={`related-${p.sku}`}
            >
              <div className="aspect-square bg-white p-3">
                <img src={p.image} alt={p.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
              </div>
              <div className="p-2 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-700 line-clamp-2 leading-tight">{p.name}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
